import { supabase } from '../supabaseClient';
import { deleteCadernoRecord } from './cadernoDeletionService';

const DEMO_MODE = 'demo';
const PRODUCTION_MODE = 'producao';
const OPTIONAL_CANCELLATION_COLUMNS = ['motivo_cancelamento'];
const OPTIONAL_EVIDENCE_COLUMNS = [
  'mime_type_original',
  'tamanho_original_bytes',
  'compressao',
];

const CADERNO_ORDER_COLUMNS = [
  'id', 'codigo', 'conta_id', 'propriedade_id', 'talhao_id', 'titulo', 'descricao',
  'tipo', 'status', 'prioridade', 'origem_tipo', 'origem_referencia', 'responsavel_id',
  'inicio_previsto', 'fim_previsto', 'inicio_real', 'fim_real', 'exige_aprovacao',
  'evidencias_exigidas', 'custo_previsto', 'parecer_aprovacao', 'aprovado_por',
  'aprovado_em', 'cancelado_por', 'cancelado_em', ...OPTIONAL_CANCELLATION_COLUMNS,
  'criado_por', 'criado_em', 'atualizado_em', 'versao',
];

const CADERNO_EVIDENCE_COLUMNS = [
  'id', 'ordem_id', 'execucao_id', 'enviado_por', 'tipo', 'titulo', 'descricao',
  'arquivo_path', 'nome_original', 'mime_type', ...OPTIONAL_EVIDENCE_COLUMNS,
  'tamanho_bytes', 'valor_texto', 'capturado_em', 'criado_em',
];

function readProjectRef(url) {
  try {
    const hostname = new URL(url).hostname;
    return hostname.endsWith('.supabase.co') ? hostname.split('.')[0] : '';
  } catch {
    return '';
  }
}

// Sem projeto de homologacao separado: a leitura/gravacao real so liga quando
// o operador confirma explicitamente o mesmo project ref do Supabase de producao.
function readRuntimeConfig() {
  const requestedMode = process.env.REACT_APP_CADERNO_DATA_MODE === PRODUCTION_MODE
    ? PRODUCTION_MODE
    : DEMO_MODE;
  const configuredProjectRef = readProjectRef(process.env.REACT_APP_SUPABASE_URL || '');
  const expectedProjectRef = (process.env.REACT_APP_CADERNO_PRODUCTION_PROJECT_REF || '').trim();
  const targetDeclaredAsProduction = process.env.REACT_APP_CADERNO_TARGET === PRODUCTION_MODE;
  const projectConfirmed = Boolean(expectedProjectRef) && configuredProjectRef === expectedProjectRef;
  const productionConfirmed = requestedMode === PRODUCTION_MODE
    && targetDeclaredAsProduction
    && projectConfirmed;
  const writesRequested = process.env.REACT_APP_CADERNO_ENABLE_WRITES === 'true';

  return Object.freeze({
    mode: productionConfirmed ? PRODUCTION_MODE : DEMO_MODE,
    requestedMode,
    configuredProjectRef,
    expectedProjectRef,
    targetDeclaredAsProduction,
    projectConfirmed,
    readsEnabled: productionConfirmed,
    writesEnabled: productionConfirmed && writesRequested,
    safetyReason: productionConfirmed
      ? (writesRequested ? 'producao_confirmada' : 'gravacoes_desligadas')
      : 'modo_demonstracao',
  });
}

export const cadernoRuntime = readRuntimeConfig();

export class CadernoSafetyError extends Error {
  constructor(message, code = 'CADERNO_SAFETY_LOCK') {
    super(message);
    this.name = 'CadernoSafetyError';
    this.code = code;
  }
}

function assertReadsEnabled() {
  if (!cadernoRuntime.readsEnabled) {
    throw new CadernoSafetyError(
      'A leitura real do Caderno está bloqueada até a confirmação do project ref de produção.',
      'CADERNO_READS_DISABLED'
    );
  }
}

function assertWritesEnabled() {
  if (!cadernoRuntime.writesEnabled) {
    throw new CadernoSafetyError(
      'A gravação real do Caderno está desligada. Os dados permanecem somente na demonstração.',
      'CADERNO_WRITES_DISABLED'
    );
  }
}

function groupBy(items, key) {
  return (items || []).reduce((result, item) => {
    const value = String(item[key]);
    result[value] = result[value] || [];
    result[value].push(item);
    return result;
  }, {});
}

function errorDescription(error) {
  return [error?.message, error?.details, error?.hint]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function isMissingCadernoOptionalColumn(error, columns) {
  const description = errorDescription(error);
  const mentionsKnownColumn = (columns || []).some(column => description.includes(String(column).toLowerCase()));
  return mentionsKnownColumn && (
    String(error?.code || '') === 'PGRST204'
    || String(error?.code || '') === '42703'
    || description.includes('column')
    || description.includes('schema cache')
  );
}

export function withoutOptionalEvidenceColumns(evidence) {
  return Object.entries(evidence || {}).reduce((result, [field, value]) => {
    if (!OPTIONAL_EVIDENCE_COLUMNS.includes(field)) result[field] = value;
    return result;
  }, {});
}

function evidenceUploadError(error) {
  const description = errorDescription(error);

  if (description.includes('row-level security') || description.includes('rls')) {
    return new CadernoSafetyError(
      'O banco recusou o anexo para este usuário. Confirme que a tarefa está ativa e que a política de anexos do Caderno foi instalada.',
      'CADERNO_EVIDENCE_PERMISSION_DENIED'
    );
  }
  if (description.includes('bucket') && (description.includes('not found') || description.includes('does not exist'))) {
    return new CadernoSafetyError(
      'O armazenamento privado de anexos do Caderno ainda não está configurado.',
      'CADERNO_EVIDENCE_BUCKET_MISSING'
    );
  }
  if (description.includes('mime') || description.includes('content type')) {
    return new CadernoSafetyError(
      'O armazenamento recusou o formato deste arquivo. Use JPEG, PNG, WebP, PDF, TXT, CSV, DOCX ou XLSX.',
      'CADERNO_EVIDENCE_MIME_REJECTED'
    );
  }

  return new CadernoSafetyError(
    `Não foi possível enviar o arquivo ao armazenamento privado.${error?.message ? ` ${error.message}` : ''}`,
    'CADERNO_EVIDENCE_UPLOAD_FAILED'
  );
}

async function selectCadernoOrders() {
  let result = await supabase
    .from('caderno_ordens_servico')
    .select(CADERNO_ORDER_COLUMNS.join(','))
    .order('atualizado_em', { ascending: false });

  if (isMissingCadernoOptionalColumn(result.error, OPTIONAL_CANCELLATION_COLUMNS)) {
    result = await supabase
      .from('caderno_ordens_servico')
      .select(CADERNO_ORDER_COLUMNS.filter(column => !OPTIONAL_CANCELLATION_COLUMNS.includes(column)).join(','))
      .order('atualizado_em', { ascending: false });
  }

  return result;
}

async function selectCadernoEvidence(orderIds) {
  let result = await supabase
    .from('caderno_evidencias')
    .select(CADERNO_EVIDENCE_COLUMNS.join(','))
    .in('ordem_id', orderIds)
    .order('criado_em', { ascending: false });

  if (isMissingCadernoOptionalColumn(result.error, OPTIONAL_EVIDENCE_COLUMNS)) {
    result = await supabase
      .from('caderno_evidencias')
      .select(CADERNO_EVIDENCE_COLUMNS.filter(column => !OPTIONAL_EVIDENCE_COLUMNS.includes(column)).join(','))
      .in('ordem_id', orderIds)
      .order('criado_em', { ascending: false });
  }

  return result;
}

async function loadUserDirectory(userIds) {
  const uniqueIds = [...new Set((userIds || []).filter(Boolean))];
  if (uniqueIds.length === 0) return {};

  const { data, error } = await supabase
    .from('usuarios')
    .select('id, nome, email')
    .in('id', uniqueIds);

  if (error) return {};

  return (data || []).reduce((directory, user) => {
    directory[String(user.id)] = user;
    return directory;
  }, {});
}

export async function listCadernoCollaborators(propertyIds) {
  assertReadsEnabled();

  const uniquePropertyIds = [...new Set((propertyIds || []).filter(Boolean).map(String))];
  if (uniquePropertyIds.length === 0) return [];

  const { data, error } = await supabase.rpc('caderno_listar_responsaveis', {
    p_propriedade_ids: uniquePropertyIds,
  });

  if (error) throw error;

  return (data || []).map(collaborator => ({
    propertyId: collaborator.propriedade_id,
    id: collaborator.usuario_id,
    name: collaborator.nome || 'Colaborador autorizado',
    permission: collaborator.permissao,
    role: collaborator.permissao === 'owner' ? 'Proprietário' : 'Editor',
    isCurrentUser: Boolean(collaborator.usuario_atual),
  }));
}

export async function listCadernoNotifications() {
  assertReadsEnabled();

  let result = await supabase
    .from('caderno_notificacoes')
    .select('id,event_key,propriedade_id,talhao_id,ordem_id,destinatario_id,tipo,prioridade,titulo,mensagem,dados,criada_em,lida_em,dispensada_em')
    .is('dispensada_em', null)
    .order('criada_em', { ascending: false });

  if (result.error && isMissingCadernoOptionalColumn(result.error, ['dispensada_em'])) {
    result = await supabase
      .from('caderno_notificacoes')
      .select('id,event_key,propriedade_id,talhao_id,ordem_id,destinatario_id,tipo,prioridade,titulo,mensagem,dados,criada_em,lida_em')
      .order('criada_em', { ascending: false });
  }

  if (result.error) throw result.error;
  return result.data || [];
}

export async function markCadernoNotificationRead(notificationId, read = true) {
  assertWritesEnabled();

  const { data, error } = await supabase
    .from('caderno_notificacoes')
    .update({ lida_em: read ? new Date().toISOString() : null })
    .eq('id', notificationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function dismissCadernoNotification(notificationId) {
  assertWritesEnabled();

  const { data, error } = await supabase
    .from('caderno_notificacoes')
    .update({ dispensada_em: new Date().toISOString() })
    .eq('id', notificationId)
    .select()
    .single();

  if (error && isMissingCadernoOptionalColumn(error, ['dispensada_em'])) {
    throw new Error('A atualização para dispensar avisos ainda não foi instalada no banco de dados.');
  }
  if (error) throw error;
  return data;
}

export async function listIrrigationNotifications() {
  assertReadsEnabled();

  const { data, error } = await supabase
    .from('notificacoes')
    .select('id,user_id,tipo,titulo,mensagem,dados,lida,criado_em')
    .eq('tipo', 'alerta')
    .contains('dados', { categoria: 'irrigacao' })
    .order('criado_em', { ascending: false })
    .limit(50);

  if (error) throw error;
  return data || [];
}

export async function markIrrigationNotificationRead(notificationId, read = true) {
  assertWritesEnabled();

  const { data, error } = await supabase
    .from('notificacoes')
    .update({ lida: read })
    .eq('id', notificationId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function listCadernoOrders() {
  assertReadsEnabled();

  const { data: orders, error: ordersError } = await selectCadernoOrders();

  if (ordersError) throw ordersError;
  if (!orders?.length) return [];

  const orderIds = orders.map(order => order.id);
  const [executionsResult, evidenceResult, historyResult] = await Promise.all([
    supabase
      .from('caderno_execucoes')
      .select('*')
      .in('ordem_id', orderIds)
      .order('criado_em', { ascending: false }),
    selectCadernoEvidence(orderIds),
    supabase
      .from('caderno_historico')
      .select('id, ordem_id, ator_id, entidade, entidade_id, acao, dados, ocorrido_em')
      .in('ordem_id', orderIds)
      .order('ocorrido_em', { ascending: false }),
  ]);

  if (executionsResult.error) throw executionsResult.error;
  if (evidenceResult.error) throw evidenceResult.error;
  if (historyResult.error) throw historyResult.error;

  const executionsByOrder = groupBy(executionsResult.data, 'ordem_id');
  const evidenceByOrder = groupBy(evidenceResult.data, 'ordem_id');
  const historyByOrder = groupBy(historyResult.data, 'ordem_id');
  const userIds = orders.flatMap(order => [order.criado_por, order.responsavel_id, order.aprovado_por]);

  (executionsResult.data || []).forEach(execution => userIds.push(execution.executor_id));
  (evidenceResult.data || []).forEach(evidence => userIds.push(evidence.enviado_por));
  (historyResult.data || []).forEach(history => userIds.push(history.ator_id));

  const users = await loadUserDirectory(userIds);

  return orders.map(order => ({
    ...order,
    executions: executionsByOrder[String(order.id)] || [],
    evidence: evidenceByOrder[String(order.id)] || [],
    history: historyByOrder[String(order.id)] || [],
    creator: users[String(order.criado_por)] || null,
    responsible: users[String(order.responsavel_id)] || null,
    approver: users[String(order.aprovado_por)] || null,
    users,
  }));
}

export async function listLegacyCadernoEvents(plotIds) {
  assertReadsEnabled();

  const uniquePlotIds = [...new Set((plotIds || []).filter(Boolean).map(String))];
  if (uniquePlotIds.length === 0) return [];

  const pageSize = 1000;
  const events = [];
  let offset = 0;

  while (true) {
    const { data, error } = await supabase
      .from('eventos_talhao')
      .select('*')
      .in('talhao_id', uniquePlotIds)
      .order('data_evento', { ascending: false })
      .range(offset, offset + pageSize - 1);

    if (error) throw error;
    events.push(...(data || []));
    if (!data || data.length < pageSize) break;
    offset += pageSize;
  }

  return events;
}

export async function createCadernoOrder(order) {
  assertWritesEnabled();

  const { data, error } = await supabase
    .from('caderno_ordens_servico')
    .insert([order])
    .select()
    .single();

  if (error) throw error;
  return data;
}

export async function updateCadernoOrderStatus(orderId, status, changes = {}) {
  assertWritesEnabled();

  const { expectedVersion = null, ...safeChanges } = changes;
  let query = supabase
    .from('caderno_ordens_servico')
    .update({ ...safeChanges, status })
    .eq('id', orderId)
    .select();

  if (expectedVersion != null) query = query.eq('versao', expectedVersion);

  const { data, error } = await query.maybeSingle();

  if (error) throw error;
  if (!data) {
    throw new CadernoSafetyError(
      'A tarefa foi alterada por outra pessoa. Atualize a tela antes de tentar novamente.',
      'CADERNO_VERSION_CONFLICT'
    );
  }
  return data;
}

const EDITABLE_ORDER_FIELDS = new Set([
  'propriedade_id',
  'talhao_id',
  'titulo',
  'descricao',
  'tipo',
  'prioridade',
  'responsavel_id',
  'inicio_previsto',
  'fim_previsto',
  'exige_aprovacao',
  'evidencias_exigidas',
  'custo_previsto',
]);

export async function updateCadernoOrder(orderId, changes, expectedVersion) {
  assertWritesEnabled();

  const safeChanges = Object.entries(changes || {}).reduce((result, [field, value]) => {
    if (EDITABLE_ORDER_FIELDS.has(field)) result[field] = value;
    return result;
  }, {});

  if (Object.keys(safeChanges).length === 0) {
    throw new CadernoSafetyError('Nenhuma altera\u00e7\u00e3o v\u00e1lida foi informada.', 'CADERNO_EMPTY_UPDATE');
  }

  let query = supabase
    .from('caderno_ordens_servico')
    .update(safeChanges)
    .eq('id', orderId)
    .select();

  if (expectedVersion != null) query = query.eq('versao', expectedVersion);

  const { data, error } = await query.maybeSingle();
  if (error) throw error;
  if (!data) {
    throw new CadernoSafetyError(
      'A tarefa foi alterada por outra pessoa. Atualize a tela e confira a vers\u00e3o mais recente.',
      'CADERNO_VERSION_CONFLICT'
    );
  }
  return data;
}

export function normalizeCadernoCancellationReason(reason) {
  return String(reason || '').trim() || 'Retirada antes do início';
}

export async function cancelCadernoOrder(orderId, reason, expectedVersion) {
  const normalizedReason = normalizeCadernoCancellationReason(reason);
  if (normalizedReason.length < 10) {
    throw new CadernoSafetyError(
      'Informe uma justificativa com pelo menos 10 caracteres.',
      'CADERNO_CANCELLATION_REASON_REQUIRED'
    );
  }

  return updateCadernoOrderStatus(orderId, 'cancelada', {
    motivo_cancelamento: normalizedReason,
    expectedVersion,
  });
}

export async function deleteCancelledCadernoOrder(orderId) {
  assertWritesEnabled();

  const { data, error } = await supabase.rpc('caderno_excluir_atividade_cancelada', {
    p_ordem_id: orderId,
  });

  if (error) throw error;
  return data;
}

export async function deleteCadernoOrder(orderId) {
  assertWritesEnabled();
  return deleteCadernoRecord('caderno_excluir_atividade', { p_ordem_id: orderId });
}

export async function deleteLegacyCadernoEvent(eventId) {
  assertWritesEnabled();
  return deleteCadernoRecord('caderno_excluir_evento', { p_evento_id: String(eventId) });
}

export async function createCadernoExecution(execution) {
  assertWritesEnabled();

  const { data, error } = await supabase
    .from('caderno_execucoes')
    .insert([execution])
    .select()
    .single();

  if (error?.code === '23505' && execution.client_request_id) {
    const { data: existing, error: existingError } = await supabase
      .from('caderno_execucoes')
      .select('*')
      .eq('client_request_id', execution.client_request_id)
      .single();

    if (existingError) throw existingError;
    return existing;
  }
  if (error) throw error;
  return data;
}

export async function uploadCadernoEvidenceFile({
  propertyId,
  orderId,
  evidenceId,
  file,
  safeName,
  mimeType,
}) {
  assertWritesEnabled();

  const path = `${propertyId}/${orderId}/${evidenceId}/${safeName}`;
  const { error } = await supabase.storage
    .from('caderno-evidencias')
    .upload(path, file, {
      cacheControl: '3600',
      contentType: mimeType,
      upsert: false,
    });

  if (error && String(error.statusCode || error.status || '') !== '409') throw evidenceUploadError(error);
  return path;
}

export async function createCadernoEvidence(evidence) {
  assertWritesEnabled();

  let { data, error } = await supabase
    .from('caderno_evidencias')
    .insert([evidence])
    .select()
    .single();

  if (isMissingCadernoOptionalColumn(error, OPTIONAL_EVIDENCE_COLUMNS)) {
    ({ data, error } = await supabase
      .from('caderno_evidencias')
      .insert([withoutOptionalEvidenceColumns(evidence)])
      .select()
      .single());
  }

  if (error?.code === '23505' && evidence.id) {
    const { data: existing, error: existingError } = await supabase
      .from('caderno_evidencias')
      .select('*')
      .eq('id', evidence.id)
      .single();

    if (existingError) throw existingError;
    if (existing.hash_sha256 !== evidence.hash_sha256 || existing.arquivo_path !== evidence.arquivo_path) {
      throw new CadernoSafetyError('Conflito de integridade ao repetir o envio da evidência.', 'CADERNO_EVIDENCE_CONFLICT');
    }
    return existing;
  }
  if (error) throw error;
  return data;
}

export async function downloadCadernoEvidenceFile(path) {
  assertReadsEnabled();
  if (!path) throw new CadernoSafetyError('O anexo n\u00e3o possui um arquivo associado.', 'CADERNO_EVIDENCE_WITHOUT_FILE');

  const { data, error } = await supabase.storage
    .from('caderno-evidencias')
    .download(path);

  if (error) throw error;
  return data;
}

export async function updateCadernoExecutionStatus(executionId, status, changes = {}) {
  assertWritesEnabled();

  const { data, error } = await supabase
    .from('caderno_execucoes')
    .update({ ...changes, status })
    .eq('id', executionId)
    .select()
    .single();

  if (error) throw error;
  return data;
}
